import { useState } from "react";
import { Camera, Trash2 } from "lucide-react";

import { api } from "@/lib/api-client";
import { shrink } from "@/lib/image";
import type { OrderFull } from "@/lib/orders";
import { useLocale } from "@/lib/locale";
import { formatDate } from "@/lib/utils";


export function IdDocuments({ order, onChanged }: { order: OrderFull; onChanged: (o: OrderFull) => void }) {
  const { locale } = useLocale();
  const [busy, setBusy] = useState<string | null>(null);
  const [bust, setBust] = useState(0);

  const slot = (side: "front" | "back") => {
    const has = side === "front" ? order.idFrontKey : order.idBackKey;
    return (
      <div className="flex flex-col gap-2">
        <span className="field-label">Emirates ID · {side}</span>
        {has ? (
          <img
            src={`${api.idDocumentUrl(order.id, side)}?v=${bust}`}
            alt={`Emirates ID ${side}`}
            className="aspect-[1.6] w-full rounded-lg border border-white/10 object-cover"
          />
        ) : (
          <div className="grid aspect-[1.6] w-full place-items-center rounded-lg border border-dashed border-white/20 text-ice/40">
            <Camera className="h-6 w-6" />
          </div>
        )}
        <div className="flex gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-ice/80 hover:border-white/40">
            <Camera className="h-3.5 w-3.5" /> {busy === side ? "Uploading…" : has ? "Retake" : "Take photo"}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={busy !== null}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setBusy(side);
                try {
                  const blob = await shrink(file);
                  onChanged(await api.uploadIdDocument(order.id, side, blob));
                  setBust((n) => n + 1);
                } finally {
                  setBusy(null);
                }
              }}
            />
          </label>
          {has && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={async () => {
                if (!window.confirm(`Delete the ${side} photo?`)) return;
                setBusy(side);
                try {
                  onChanged(await api.deleteIdDocument(order.id, side));
                } finally {
                  setBusy(null);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-pink"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="glass-panel mt-4 p-5">
      <div className="brand-kicker">Emirates ID (optional)</div>
      <p className="mt-1 text-xs text-ice/60">
        Stored privately, never printed on the order. Photos are shrunk on your device before upload
        {order.idDeleteAfter ? ` and deleted automatically on ${formatDate(order.idDeleteAfter, locale)}.` : "."}
      </p>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {slot("front")}
        {slot("back")}
      </div>
    </section>
  );
}
